'use strict';

/**
 * This is a namespaced port of https://github.com/tristen/hoverintent,
 * with slight modification to accept selector with dynamically added element in dom,
 * instead of just already present element.
 *
 * @param {HTMLElement} parent
 * @param {string} selector
 * @param {CallableFunction} onOver
 * @param {CallableFunction} onOut
 *
 * @return {object}
 */
export default function(parent, selector, onOver, onOut) {
  let x; let y; let pX; let pY;
  let mouseOver = false;
  let focused = false;
  const h = {};
  let state = 0;
  let timer = 0;

  let options = {
    sensitivity: 7,
    interval: 100,
    timeout: 0,
    handleFocus: false,
  };

  function delay(el, e) {
    if (timer) {
      timer = clearTimeout(timer);
    }

    state = 0;
    return focused ? undefined : onOut(el, e);
  }

  function tracker(e) {
    x = e.clientX;
    y = e.clientY;
  }

  function compare(el, e) {
    if (timer) timer = clearTimeout(timer);
    if ((Math.abs(pX - x) + Math.abs(pY - y)) < options.sensitivity) {
      state = 1;
      return focused ? undefined : onOver(el, e);
    } else {
      pX = x;
      pY = y;
      timer = setTimeout(function() {
        compare(el, e);
      }, options.interval);
    }
  }

  // Public methods
  h.options = function(opt) {
    const focusOptionChanged = opt.handleFocus !== options.handleFocus;
    options = Object.assign({}, options, opt);
    if (focusOptionChanged) {
            options.handleFocus ? addFocus() : removeFocus();
    }

    return h;
  };

  function dispatchOver(el, e) {
    mouseOver = true;
    if (timer) {
      timer = clearTimeout(timer);
    }

    el.removeEventListener('mousemove', tracker, false);

    if (state !== 1) {
      pX = e.clientX;
      pY = e.clientY;

      el.addEventListener('mousemove', tracker, false);

      timer = setTimeout(function() {
        compare(el, e);
      }, options.interval);
    }

    return this;
  }

  /**
   * Newly added method,
   * A wrapper around dispatchOver to support dynamically added elements to dom
   */
  function onMouseOver(event) {
    if (event.target.matches(selector + ', ' + selector + ' *')) {
      dispatchOver(event.target.closest(selector), event);
    }
  };

  function dispatchOut(el, e) {
    mouseOver = false;
    if (timer) {
      timer = clearTimeout(timer);
    }

    el.removeEventListener('mousemove', tracker, false);

    if (state === 1) {
      timer = setTimeout(function() {
        delay(el, e);
      }, options.timeout);
    }

    return this;
  }

  /**
   * Newly added method,
   * A wrapper around dispatchOut to support dynamically added elements to dom
   */
  function onMouseOut(event) {
    if (event.target.matches(selector + ', ' + selector + ' *')) {
      dispatchOut(event.target.closest(selector), event);
    }
  };

  function dispatchFocus(el, e) {
    if (!mouseOver) {
      focused = true;
      onOver(el, e);
    }
  }

  /**
   * Newly added method,
   * A wrapper around dispatchFocus to support dynamically added elements to dom
   */
  function onFocus(event) {
    if (event.target.matches(selector + ', ' + selector + ' *')) {
      dispatchFocus(event.target.closest(selector), event);
    }
  };

  function dispatchBlur(el, e) {
    if (!mouseOver && focused) {
      focused = false;
      onOut(el, e);
    }
  }

  /**
   * Newly added method,
   * A wrapper around dispatchBlur to support dynamically added elements to dom
   */
  function onBlur(event) {
    if (event.target.matches(selector + ', ' + selector + ' *')) {
      dispatchBlur(event.target.closest(selector), event);
    }
  };

  /**
   * Modified to support dynamically added element
   */
  function addFocus() {
    parent.addEventListener('focus', onFocus, false);
    parent.addEventListener('blur', onBlur, false);
  }

  /**
   * Modified to support dynamically added element
   */
  function removeFocus() {
    parent.removeEventListener('focus', onFocus, false);
    parent.removeEventListener('blur', onBlur, false);
  }

  /**
   * Modified to support dynamically added element
   */
  h.remove = function() {
    if (!parent) {
      return;
    }

    parent.removeEventListener('mouseover', onMouseOver, false);
    parent.removeEventListener('mouseout', onMouseOut, false);
    removeFocus();
  };

  /**
   * Modified to support dynamically added element
   */
  if (parent) {
    parent.addEventListener('mouseover', onMouseOver, false);
    parent.addEventListener('mouseout', onMouseOut, false);
  }

  return h;
};
